/**
 * MathUnitOverview — Phase 90
 *
 * Drop-in panel for the Math Engine page. Shows every unit the teacher
 * is working on with a coverage progress bar, plus surfaces the two
 * assessment checkpoints:
 *   • Mid-Unit Check-In (40-60% coverage band, after first exit ticket)
 *   • Wrap Up Unit (>=90% coverage)
 *
 * The component is self-contained: it polls /api/math-engine/my-units
 * on mount + whenever `refreshKey` changes, manages its own modal
 * state, and emits navigation only when a teacher picks the
 * "End-of-Unit Assignment" path (which routes to the Summative Task
 * Designer with URL-prefilled expectations).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Award,
  ClipboardCheck,
  ClipboardList,
  Loader2,
  Sparkles,
  Trash2,
  Wand2,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import {
  deleteMyMathUnit,
  dismissMathMidQuiz,
  generateMathEndUnitTest,
  generateMathMidQuiz,
  listMyMathUnits,
  markMathMidQuizOffered,
  recordMathMidQuizResult,
  recordMathUnitWrapup,
} from "../lib/api";

const UnitCard = ({ unit, onOpenMidQuiz, onOpenWrapup, onLogMidQuizResults, onDelete }) => {
  const pct = unit.coverage_pct || 0;
  const barColor =
    pct < 40 ? "bg-[#4a6984]/60" : pct < 90 ? "bg-[#1c2d3d]" : "bg-emerald-600";
  // Phase 91 — the mid-quiz has been generated, but the teacher hasn't
  // logged the class's results yet. Surface a Log Results CTA so the
  // adaptive remediation pipeline has something to feed Lesson 2.
  const needsResultLog =
    !!unit.mid_quiz?.activity_id && !unit.mid_quiz?.result_summary;
  const hasRemediation =
    Array.isArray(unit.mid_quiz?.remediation) &&
    unit.mid_quiz.remediation.length > 0;
  return (
    <div
      data-testid={`math-unit-card-${unit.unit_id}`}
      className="rounded-lg border border-[#1c2d3d]/15 bg-white p-4 space-y-2"
    >
      <div className="flex items-baseline justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-[#4a6984]/70">
            {unit.grade}
          </p>
          <h4 className="font-extrabold text-[#1c2d3d] text-base leading-tight truncate">
            {unit.name}
          </h4>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs font-bold text-[#1c2d3d]">
            {unit.covered_expectations.length}/{unit.target_expectations.length} expectations
          </span>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDelete?.(unit); }}
            aria-label={`Delete ${unit.name}`}
            data-testid={`math-unit-delete-${unit.unit_id}`}
            className="h-7 w-7 rounded-md grid place-items-center text-[#4a6984]/55 hover:text-rose-600 hover:bg-rose-50 transition"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <div className="h-2 rounded-full bg-[#1c2d3d]/10 overflow-hidden">
        <div
          className={`${barColor} h-full transition-all duration-500`}
          style={{ width: `${Math.min(100, pct)}%` }}
          data-testid={`math-unit-progress-${unit.unit_id}`}
        />
      </div>
      <div className="flex items-center justify-between text-[11px] text-[#4a6984]/75">
        <span>{pct.toFixed(0)}% covered</span>
        <span>
          {unit.lesson_count} lesson{unit.lesson_count === 1 ? "" : "s"} ·{" "}
          {unit.exit_ticket_count} exit ticket{unit.exit_ticket_count === 1 ? "" : "s"}
        </span>
      </div>
      <div className="flex flex-wrap gap-2 pt-1">
        {unit.should_offer_mid_quiz && (
          <button
            type="button"
            onClick={() => onOpenMidQuiz(unit)}
            data-testid={`math-unit-mid-quiz-${unit.unit_id}`}
            className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-md bg-[#96ded1] text-[#1c2d3d] hover:bg-[#7bd1c1]"
          >
            <ClipboardCheck className="h-3.5 w-3.5" /> Mid-Unit Check-In
          </button>
        )}
        {needsResultLog && (
          <button
            type="button"
            onClick={() => onLogMidQuizResults(unit)}
            data-testid={`math-unit-log-results-${unit.unit_id}`}
            className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-md bg-amber-100 text-amber-900 border border-amber-300 hover:bg-amber-200"
          >
            <Wand2 className="h-3.5 w-3.5" /> Log mid-quiz results
          </button>
        )}
        {unit.should_offer_wrapup && (
          <button
            type="button"
            onClick={() => onOpenWrapup(unit)}
            data-testid={`math-unit-wrapup-${unit.unit_id}`}
            className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-md bg-[#1c2d3d] text-[#96ded1] hover:bg-[#0f1a24]"
          >
            <Award className="h-3.5 w-3.5" /> Wrap Up Unit
          </button>
        )}
        {unit.mid_quiz?.activity_id && !needsResultLog && !hasRemediation && (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-[#96ded1]/20 text-[#1c2d3d] border border-[#96ded1]/60">
            Mid-Quiz Generated
          </span>
        )}
        {hasRemediation && (
          <span
            data-testid={`math-unit-adaptive-${unit.unit_id}`}
            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300"
            title="The next math lesson in this unit will incorporate these results"
          >
            <Wand2 className="h-3 w-3" /> Adaptive Context Active ·{" "}
            {unit.mid_quiz.remediation.length}
          </span>
        )}
        {unit.wrapup?.kind && (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-900 border border-emerald-300">
            Wrapped: {unit.wrapup.kind}
          </span>
        )}
      </div>
    </div>
  );
};

const MathUnitOverview = ({ refreshKey = 0 }) => {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // unit_id while a server call runs
  const [midQuizUnit, setMidQuizUnit] = useState(null);
  const [wrapupUnit, setWrapupUnit] = useState(null);
  // Phase 91 — adaptive remediation capture dialog. Holds the unit
  // whose mid-quiz results the teacher is logging right now + the
  // textarea contents.
  const [logResultsUnit, setLogResultsUnit] = useState(null);
  const [resultsSummary, setResultsSummary] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const refresh = async () => {
    try {
      const data = await listMyMathUnits();
      setUnits(Array.isArray(data) ? data : []);
    } catch (e) {
      // 403 = non-Pro+ user → hide silently. Other errors → log only.
      const status = e?.response?.status;
      if (status !== 403) console.warn("listMyMathUnits failed", e);
      setUnits([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [refreshKey]);

  // Auto-pop the mid-quiz when an eligible unit appears and we haven't
  // already shown it to the teacher.
  useEffect(() => {
    const triggerable = units.find(
      (u) => u.should_offer_mid_quiz && !u.mid_quiz?.offered_at,
    );
    if (triggerable && !midQuizUnit && !wrapupUnit) {
      setMidQuizUnit(triggerable);
      markMathMidQuizOffered(triggerable.unit_id).catch(() => {});
    }
  }, [units, midQuizUnit, wrapupUnit]);

  const handleGenerateMidQuiz = async (unit) => {
    setBusy(unit.unit_id);
    try {
      const { activity } = await generateMathMidQuiz(unit.unit_id);
      toast.success("Mid-unit quiz generated — saved to your Library.", {
        description: activity.activity_name,
      });
      setMidQuizUnit(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate mid-unit quiz");
    } finally {
      setBusy(null);
    }
  };

  const handleDismissMidQuiz = async (unit) => {
    try {
      await dismissMathMidQuiz(unit.unit_id);
    } catch (_) {
      /* non-fatal */
    }
    setMidQuizUnit(null);
    await refresh();
  };

  const handleGenerateEndTest = async (unit) => {
    setBusy(unit.unit_id);
    try {
      const { activity } = await generateMathEndUnitTest(unit.unit_id);
      toast.success("End-of-unit test generated — saved to your Library.", {
        description: activity.activity_name,
      });
      setWrapupUnit(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate end-of-unit test");
    } finally {
      setBusy(null);
    }
  };

  const handleEndOfUnitAssignment = async (unit) => {
    setBusy(unit.unit_id);
    try {
      await recordMathUnitWrapup(unit.unit_id, "assignment");
    } catch (_) {
      /* still allow nav even if recording fails */
    } finally {
      setBusy(null);
    }
    // Route to the Summative Task Designer with expectations preloaded.
    // Derive the strand code from the first expectation (e.g. "B1.1" → "B")
    // so the form lands with the strand dropdown already pinned — that's
    // what gates the expectation checklist render.
    const firstCode = (unit.target_expectations || []).find((c) => /^[A-Z]/.test(c));
    const strandCode = firstCode ? firstCode[0] : "";
    const params = new URLSearchParams({
      mode: "summative",
      grade: unit.grade,
      subject: "Mathematics",
      ...(strandCode ? { strand: strandCode } : {}),
      expectations: unit.target_expectations.join(","),
    });
    setWrapupUnit(null);
    navigate(`/generator?${params.toString()}`);
  };

  const handleSkipWrapup = async (unit) => {
    try {
      await recordMathUnitWrapup(unit.unit_id, "skip");
    } catch (_) {
      /* non-fatal */
    }
    setWrapupUnit(null);
    await refresh();
  };

  // Phase 91 — open the Log Results dialog. Pre-fills the textarea with
  // any earlier draft so a teacher can iterate.
  const handleOpenLogResults = (unit) => {
    setLogResultsUnit(unit);
    setResultsSummary(unit?.mid_quiz?.result_summary || "");
  };

  const handleSubmitMidQuizResults = async () => {
    if (!logResultsUnit) return;
    const summary = (resultsSummary || "").trim();
    if (!summary) {
      toast.error("Add a short note about how the class did before saving.");
      return;
    }
    setBusy(logResultsUnit.unit_id);
    try {
      const res = await recordMathMidQuizResult(logResultsUnit.unit_id, summary);
      const count = Array.isArray(res?.remediation) ? res.remediation.length : 0;
      if (count > 0) {
        toast.success(
          `Adaptive context saved — ${count} expectation${count === 1 ? "" : "s"} flagged.`,
          {
            description:
              "The next math lesson in this unit will revisit or extend these.",
          }
        );
      } else {
        toast.success("Mid-quiz results saved.");
      }
      setLogResultsUnit(null);
      setResultsSummary("");
      await refresh();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not save mid-quiz results"
      );
    } finally {
      setBusy(null);
    }
  };

  if (loading) return null;
  if (units.length === 0) return null;

  return (
    <section
      data-testid="math-unit-overview"
      className="mt-8 rounded-xl border border-[#1c2d3d]/15 bg-[#f4f5f7] p-5 sm:p-6"
    >
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <p className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-[#4a6984]/70">
            Math Engine
          </p>
          <h3 className="font-extrabold text-[#1c2d3d] text-lg">Your active units</h3>
        </div>
        <p className="text-xs text-[#4a6984]/70 max-w-sm text-right">
          Progress is computed from the expectations every lesson you generate
          targets.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {units.map((u) => (
          <UnitCard
            key={u.unit_id}
            unit={u}
            onOpenMidQuiz={setMidQuizUnit}
            onOpenWrapup={setWrapupUnit}
            onLogMidQuizResults={handleOpenLogResults}
            onDelete={setDeleteTarget}
          />
        ))}
      </div>

      {/* Mid-unit check-in dialog */}
      <Dialog
        open={!!midQuizUnit}
        onOpenChange={(o) => !o && setMidQuizUnit(null)}
      >
        <DialogContent
          className="max-w-lg"
          data-testid="math-mid-quiz-dialog"
        >
          <DialogHeader>
            <DialogTitle>Mid-Unit Check-In ready?</DialogTitle>
            <DialogDescription>
              You've covered{" "}
              <strong>
                {midQuizUnit?.covered_expectations.length} of{" "}
                {midQuizUnit?.target_expectations.length}
              </strong>{" "}
              expectations in {midQuizUnit?.name}. A short 10-15 question
              diagnostic now will tell you where the class is before you push
              into the rest of the unit.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => midQuizUnit && handleDismissMidQuiz(midQuizUnit)}
              data-testid="math-mid-quiz-dismiss"
              disabled={!!busy}
            >
              Not right now
            </Button>
            <Button
              onClick={() => midQuizUnit && handleGenerateMidQuiz(midQuizUnit)}
              data-testid="math-mid-quiz-generate"
              disabled={!!busy}
              className="bg-[#1c2d3d] hover:bg-[#0f1a24] text-white"
            >
              {busy === midQuizUnit?.unit_id ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" /> Generate Mid-Unit Quiz
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* End-of-unit wrap-up dialog */}
      <Dialog
        open={!!wrapupUnit}
        onOpenChange={(o) => !o && setWrapupUnit(null)}
      >
        <DialogContent
          className="max-w-xl"
          data-testid="math-wrapup-dialog"
        >
          <DialogHeader>
            <DialogTitle>Wrap Up {wrapupUnit?.name}</DialogTitle>
            <DialogDescription>
              You've covered {wrapupUnit?.coverage_pct?.toFixed(0)}% of this
              unit. Pick how the students will show their learning:
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
            <button
              type="button"
              onClick={() => wrapupUnit && handleGenerateEndTest(wrapupUnit)}
              data-testid="math-wrapup-test"
              disabled={!!busy}
              className="rounded-lg border-2 border-[#1c2d3d]/15 hover:border-[#96ded1] p-4 text-left transition-colors disabled:opacity-50"
            >
              <ClipboardList className="h-5 w-5 text-[#1c2d3d] mb-2" />
              <p className="font-extrabold text-[#1c2d3d] text-sm">End-of-Unit Test</p>
              <p className="text-xs text-[#4a6984]/75 mt-1">
                20-30 questions, organised by topic, with point values.
              </p>
            </button>
            <button
              type="button"
              onClick={() => wrapupUnit && handleEndOfUnitAssignment(wrapupUnit)}
              data-testid="math-wrapup-assignment"
              disabled={!!busy}
              className="rounded-lg border-2 border-[#1c2d3d]/15 hover:border-[#96ded1] p-4 text-left transition-colors disabled:opacity-50"
            >
              <Award className="h-5 w-5 text-[#1c2d3d] mb-2" />
              <p className="font-extrabold text-[#1c2d3d] text-sm">End-of-Unit Assignment</p>
              <p className="text-xs text-[#4a6984]/75 mt-1">
                Open the Summative Task Designer with these expectations
                preloaded.
              </p>
            </button>
            <button
              type="button"
              onClick={() => wrapupUnit && handleSkipWrapup(wrapupUnit)}
              data-testid="math-wrapup-skip"
              disabled={!!busy}
              className="rounded-lg border-2 border-[#1c2d3d]/15 hover:border-[#4a6984]/40 p-4 text-left transition-colors disabled:opacity-50"
            >
              <p className="font-extrabold text-[#1c2d3d] text-sm">Skip for now</p>
              <p className="text-xs text-[#4a6984]/75 mt-1">
                Use the rubric and observations only.
              </p>
            </button>
          </div>
          {busy === wrapupUnit?.unit_id && (
            <p className="text-xs text-[#4a6984]/70 flex items-center gap-1 mt-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Working…
            </p>
          )}
        </DialogContent>
      </Dialog>

      {/* Phase 91 — Log mid-quiz results dialog */}
      <Dialog
        open={!!logResultsUnit}
        onOpenChange={(o) => {
          if (!o) {
            setLogResultsUnit(null);
            setResultsSummary("");
          }
        }}
      >
        <DialogContent
          className="max-w-xl"
          data-testid="math-log-results-dialog"
        >
          <DialogHeader>
            <DialogTitle>
              How did the class do on the mid-unit check-in?
            </DialogTitle>
            <DialogDescription>
              Type a quick class-level snapshot. Mention specific expectation
              codes (e.g. <strong>B1.4</strong>) the class struggled with or
              mastered. We&apos;ll turn it into adaptive context so the next
              math lesson in <strong>{logResultsUnit?.name}</strong> revisits
              the gaps and stretches the on-track topics.
            </DialogDescription>
          </DialogHeader>
          {logResultsUnit?.target_expectations?.length ? (
            <div className="text-[11px] text-[#4a6984]/70 -mt-2">
              <span className="font-bold uppercase tracking-[0.15em]">
                In-scope codes:
              </span>{" "}
              {logResultsUnit.target_expectations.join(", ")}
            </div>
          ) : null}
          <Textarea
            data-testid="math-log-results-textarea"
            rows={6}
            value={resultsSummary}
            onChange={(e) => setResultsSummary(e.target.value)}
            placeholder={
              "e.g. Most of the class is solid on B1.1 and B1.2.\nB1.4 (place value with decimals) was rough — about a third still need a re-teach.\nB1.6 was easy — a small group is ready for an extension."
            }
            className="bg-[#f4f5f7] border border-[#4a6984]/20 text-[#1c2d3d] font-medium focus-visible:ring-2 focus-visible:ring-[#96ded1] rounded-md resize-none"
          />
          <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setLogResultsUnit(null);
                setResultsSummary("");
              }}
              data-testid="math-log-results-cancel"
              disabled={!!busy}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitMidQuizResults}
              data-testid="math-log-results-submit"
              disabled={!!busy || !resultsSummary.trim()}
              className="bg-[#1c2d3d] hover:bg-[#0f1a24] text-white"
            >
              {busy === logResultsUnit?.unit_id ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Deriving
                  adaptive plan…
                </>
              ) : (
                <>
                  <Wand2 className="h-4 w-4 mr-2" /> Save & shape next lesson
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete-unit confirm dialog (Phase 99). Wipes every artifact
          the teacher has generated for this SCDSB unit + the local
          unit-state doc, then refreshes the list. */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#1c2d3d]">
              Delete this Math Engine unit?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#4a6984]">
              <span className="font-bold text-[#1c2d3d]">
                {deleteTarget ? `${deleteTarget.grade} · ${deleteTarget.name}` : ""}
              </span>{" "}
              and every lesson, exit ticket, mid-quiz, and wrap-up
              assessment generated under it will be permanently removed
              from your Library. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async (e) => {
                e.preventDefault();
                if (!deleteTarget) return;
                setDeleting(true);
                try {
                  const res = await deleteMyMathUnit(deleteTarget.unit_id);
                  toast.success(
                    `Removed ${deleteTarget.name}`,
                    {
                      description: `${res?.deleted_activities || 0} lesson${(res?.deleted_activities || 0) === 1 ? "" : "s"} deleted.`,
                    },
                  );
                  setDeleteTarget(null);
                  await refresh();
                } catch (err) {
                  toast.error(
                    err?.response?.data?.detail || "Could not delete this unit.",
                  );
                } finally {
                  setDeleting(false);
                }
              }}
              disabled={deleting}
              data-testid="math-unit-delete-confirm"
              className="bg-rose-600 hover:bg-rose-700 text-white font-extrabold"
            >
              {deleting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Deleting…</>
              ) : (
                "Delete unit"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};

export default MathUnitOverview;
